import createDOMPurify from 'dompurify';
import { SecureLogger } from './secureLogger';

const DOMPurify = createDOMPurify(window as any);

// Configuration stricte de DOMPurify pour la sécurité
const PURIFY_CONFIG = {
  ALLOWED_TAGS: ['strong', 'em', 'b', 'i', 'u', 'br', 'p'],
  ALLOWED_ATTR: [],
  FORBID_TAGS: ['script', 'iframe', 'img', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'javascript:', 'vbscript:'],
  KEEP_CONTENT: true
};

export class SecurityUtils {
  // Fonction pour la saisie en temps réel (pas de trim)
  static sanitizeInputLive(input: string): string {
    if (!input || typeof input !== 'string') {
      return '';
    }
    
    // Première passe : nettoyage avec DOMPurify
    let sanitized = DOMPurify.sanitize(input, PURIFY_CONFIG);
    
    // Deuxième passe : suppression manuelle des patterns dangereux
    sanitized = sanitized
      .replace(/<img[^>]*>/gi, '')
      .replace(/<iframe[^>]*>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/vbscript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .replace(/<script[^>]*>.*?<\/script>/gi, '')
      .replace(/\u200B/g, '')
      .replace(/\uFEFF/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\u2028/g, '\n')  // ← GARDER comme retour à la ligne
      .replace(/\u2029/g, '\n'); // ← GARDER comme retour à la ligne
    
    // NOUVELLE logique : préserver les retours à la ligne
    sanitized = sanitized
      .replace(/[ \t]+/g, ' ')              // ← Normaliser SEULEMENT espaces horizontaux
      .replace(/\n{4,}/g, '\n\n\n');        // ← Limiter à max 3 retours consécutifs
    
    return sanitized;
  }

  // Fonction pour la soumission/sauvegarde (avec trim)
  static sanitizeInputForSubmission(input: string): string {
    const sanitized = this.sanitizeInputLive(input);
    return sanitized.trim(); // Trim seulement à la soumission
  }

  // Nouvelle méthode : validation stricte pour les champs critiques
  static sanitizeCriticalInput(input: string): string {
    // IMPORTANT: Vérifier l'input BRUT avant sanitisation
    const suspiciousPatterns = [
      /<script[^>]*>/gi,  // Balises script
      /javascript:/gi,    // Protocoles dangereux
      /on\w+\s*=/gi,     // Événements
      /data:text\/html/gi, // Data URLs HTML
      /vbscript:/gi      // VBScript
    ];
    
    // Rejeter AVANT sanitisation si contenu suspect détecté
    if (suspiciousPatterns.some(pattern => pattern.test(input))) {
      throw new Error('Suspicious content detected');
    }
    
    const sanitized = this.sanitizeInputForSubmission(input);
    
    // Validation supplémentaire pour les champs critiques
    if (sanitized.length > 1000) {
      throw new Error('Input too long');
    }
    
    return sanitized;
  }

  // Validation d'URL sécurisée
  static validateUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  }

  /**
   * Normalise et sécurise une URL externe destinée à un href.
   * Ajoute https:// si le scheme manque, puis valide http/https.
   * Neutralise les schemes dangereux (javascript:, data:, etc.) en
   * retournant '#'. À utiliser pour tout href issu de contenu utilisateur.
   */
  static toSafeHref(url: string | null | undefined): string {
    if (!url || typeof url !== 'string') return '#';
    const trimmed = url.trim();
    if (!trimmed) return '#';
    // Scheme explicite (ex: "javascript:", "data:", "https:") → doit être http/https
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
    if (hasScheme) {
      return this.validateUrl(trimmed) ? trimmed : '#';
    }
    // Pas de scheme → préfixer https:// puis valider
    const normalized = `https://${trimmed}`;
    return this.validateUrl(normalized) ? normalized : '#';
  }

  // Validation d'email renforcée
  static validateEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) && email.length <= 254;
  }

  // Validation de coordonnées GPS
  static validateCoordinates(lat: number, lng: number): boolean {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  }

  // Nettoyage des données d'événement
  static sanitizeEventData(data: any): any {
    return {
      ...data,
      title: this.sanitizeInputForSubmission(data.title || ''),
      description: this.sanitizeInputForSubmission(data.description || ''),
      location: this.sanitizeInputForSubmission(data.location || ''),
      locationName: this.sanitizeInputForSubmission(data.locationName || ''),
      organizerContact: this.sanitizeInputForSubmission(data.organizerContact || '')
    };
  }

  // 🔒 Validation du nom de fichier
  static validateFileName(fileName: string): boolean {
    if (!fileName || typeof fileName !== 'string') {
      return false;
    }

    const dangerousExtensions = [
      '.js', '.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.jar', '.php', '.asp', '.aspx'
    ];

    const dangerousPatterns = [
      /[<>:"|?*]/,
      /\.\./,
      /^\./,
      /\.$/,
      /[\/\\]/,
    ];

    const hasDangerousPattern = dangerousPatterns.some(pattern => pattern.test(fileName));
    const hasDangerousExtension = dangerousExtensions.some(ext => 
      fileName.toLowerCase().endsWith(ext)
    );

    // Utiliser le logger sécurisé
    SecureLogger.debug('validateFileName', {
      fileName,
      hasDangerousPattern,
      hasDangerousExtension
    });

    return !hasDangerousPattern && !hasDangerousExtension;
  }

  //  Validation du type MIME
  static validateMimeType(mimeType: string, allowedTypes: string[]): boolean {
    return allowedTypes.includes(mimeType);
  }

  // 🔒 Validation de la taille de fichier
  static validateFileSize(fileSize: number, maxSize: number): boolean {
    return fileSize <= maxSize;
  }

  // Sanitize HTML for safe rendering (dangerouslySetInnerHTML)
  static sanitizeHtml(html: string): string {
    if (!html || typeof html !== 'string') {
      return '';
    }
    return DOMPurify.sanitize(html, PURIFY_CONFIG);
  }

  // 🔒 Nettoyage du nom de fichier
  static sanitizeFileName(fileName: string): string {
    if (!fileName || typeof fileName !== 'string') {
      return '';
    }

    return fileName
      .replace(/[<>:"|?*]/g, '_')  // Remplacer les caractères dangereux
      .replace(/\.\./g, '.')       // Éviter la navigation (.. → .)
      .replace(/^\.+/, '')         // Supprimer TOUS les points initiaux
      .replace(/\.$/, '')          // Supprimer le point final
      .replace(/[\/\\]/g, '_')     // Remplacer les séparateurs de chemin
      .substring(0, 255);          // Limiter la longueur
  }
}
